/**
 * Cortex Policies panel — the scope-parameterized list + dialogs shared by
 * the global Cortex Policies page and the org detail Policies section
 * (GDN-2407). The page/section owns its chrome (header, description); this
 * panel owns the state filter, table, and create/edit dialogs.
 *
 * scope='global' lists/creates global rows; scope='organization' requires
 * organizationId and lists/creates that org's rows. The edit dialog is
 * scope-agnostic (policies are edited by id; supersede clones scope).
 */

import { useMemo, useState } from 'react';
import { Add, ContentCopy, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

import { isSchemaSupported, SchemaForm } from './schema-form';
import {
  useCreateCortexPolicyMutation,
  useDeprecateCortexPolicyMutation,
  useGetCortexPolicyQuery,
  useGetCortexPolicyTypeSchemaQuery,
  useListCortexPoliciesQuery,
  useListCortexPolicyTypesQuery,
  usePromoteCortexPolicyMutation,
  useSupersedeCortexPolicyMutation,
  useUpdateCortexPolicyMutation,
} from '../store/api';
import { formatDateTime } from '../utils/formatting';

type PolicyState = 'draft' | 'active' | 'deprecated';

export type PolicyEditorScope =
  | { scope: 'global'; organizationId?: undefined }
  | { scope: 'organization'; organizationId: string };

const STATE_OPTIONS: Array<{ value: '' | PolicyState; label: string }> = [
  { value: '', label: 'All states' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'deprecated', label: 'Deprecated' },
];

function stateChipColor(state: string): 'default' | 'info' | 'success' | 'warning' {
  if (state === 'active') return 'success';
  if (state === 'draft') return 'info';
  if (state === 'deprecated') return 'warning';
  return 'default';
}

export type CortexPoliciesPanelProps = PolicyEditorScope;

export function CortexPoliciesPanel(props: CortexPoliciesPanelProps) {
  const { scope, organizationId } = props;
  const [stateFilter, setStateFilter] = useState<'' | PolicyState>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);

  const { data, isLoading, isFetching, error } = useListCortexPoliciesQuery({
    scope,
    ...(scope === 'organization' ? { organizationId } : {}),
    state: stateFilter || undefined,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Select
          size="small"
          value={stateFilter}
          onChange={e => setStateFilter(e.target.value as '' | PolicyState)}
          sx={{ minWidth: 160 }}
        >
          {STATE_OPTIONS.map(opt => (
            <MenuItem key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
        >
          New Policy
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load policies. Try refreshing.
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Applies To</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Citations Verified</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(isLoading || isFetching) && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Loading policies…
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No policies yet. Create one with the &ldquo;New Policy&rdquo; button.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map(p => {
                return (
                  <TableRow
                    key={p.policy_id}
                    hover
                    onClick={() => setEditingPolicyId(p.policy_id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {p.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {p.policy_type ? (
                        <Chip
                          label={p.policy_type}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.applies_to ? (
                        <Chip
                          label={p.applies_to}
                          color="info"
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.state}
                        color={stateChipColor(p.state)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {p.citations_verified ? (
                        <Chip label="Verified" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Unverified" color="default" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(new Date(p.created_at))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(new Date(p.updated_at))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            setEditingPolicyId(p.policy_id);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {createOpen && (
        <CreatePolicyDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          scope={scope}
          organizationId={organizationId}
        />
      )}

      {editingPolicyId && (
        <PolicyDialog
          policyId={editingPolicyId}
          onClose={() => setEditingPolicyId(null)}
          onOpenPolicy={id => setEditingPolicyId(id)}
        />
      )}
    </>
  );
}

interface CreatePolicyDialogProps {
  open: boolean;
  onClose: () => void;
  scope: 'global' | 'organization';
  organizationId?: string;
}

function CreatePolicyDialog({ open, onClose, scope, organizationId }: CreatePolicyDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [appliesTo, setAppliesTo] = useState(NO_FLOW);
  const [policyType, setPolicyType] = useState('');
  const [metadataText, setMetadataText] = useState('');
  const [metadataParseError, setMetadataParseError] = useState<string | null>(null);
  const [createPolicy, { isLoading }] = useCreateCortexPolicyMutation();

  // Flow first, then type: changing the flow resets the type so a stale
  // selection from another flow can't linger.
  const handleAppliesToChange = (flow: string) => {
    setAppliesTo(flow);
    setPolicyType('');
  };

  const handleMetadataChange = (text: string) => {
    setMetadataText(text);
    if (text.trim() === '') {
      setMetadataParseError(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setMetadataParseError('Metadata must be a JSON object.');
      } else {
        setMetadataParseError(null);
      }
    } catch (e: any) {
      setMetadataParseError(e?.message || 'Invalid JSON.');
    }
  };

  const missingRequiredMetadata = useMissingRequiredMetadataFields(
    policyType || null,
    metadataText,
  );

  const missingFields = [
    ...(title.trim().length === 0 ? ['Title'] : []),
    ...(bodyMd.trim().length === 0 ? ['Body'] : []),
    // A flow is chosen by picking a type — an untyped policy would never be
    // tagged with (or read by) the selected flow.
    ...(appliesTo !== NO_FLOW && !policyType ? ['Policy type'] : []),
    ...missingRequiredMetadata,
  ];

  const canSubmit =
    missingFields.length === 0 && !metadataParseError && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const parsedMetadata =
      metadataText.trim() === '' ? undefined : JSON.parse(metadataText);
    try {
      await createPolicy({
        createPolicyRequest: {
          title: title.trim(),
          body_md: bodyMd,
          ...(scope === 'organization' && organizationId
            ? { scope, organization_id: organizationId }
            : {}),
          ...(parsedMetadata !== undefined ? { metadata: parsedMetadata } : {}),
          ...(policyType ? { policy_type: policyType } : {}),
        },
      }).unwrap();
      enqueueSnackbar('Policy created', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to create policy', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {scope === 'organization' ? 'New Organization Policy' : 'New Policy'}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            autoFocus
            fullWidth
            size="small"
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Body&nbsp;*
            </Typography>
            <TextField
              value={bodyMd}
              onChange={e => setBodyMd(e.target.value)}
              fullWidth
              multiline
              minRows={10}
              placeholder="Markdown body..."
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.875rem' } } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <AppliesToSelect value={appliesTo} onChange={handleAppliesToChange} />
            <PolicyTypeSelect
              value={policyType}
              onChange={setPolicyType}
              allowUntyped={appliesTo === NO_FLOW}
              flowFilter={appliesTo}
            />
          </Box>
          <PolicyMetadataEditor
            policyType={policyType || null}
            metadataText={metadataText}
            parseError={metadataParseError}
            onMetadataTextChange={handleMetadataChange}
            helperWhenValid="Optional — leave empty for no metadata."
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          {isLoading
            ? ''
            : metadataParseError
              ? 'Fix the metadata JSON to continue.'
              : missingFields.length > 0
                ? `Required: ${missingFields.join(', ')}`
                : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
            {isLoading ? <CircularProgress size={18} /> : 'Create draft'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

interface PolicyDialogProps {
  policyId: string;
  onClose: () => void;
  onOpenPolicy: (id: string) => void;
}

function PolicyDialog({ policyId, onClose, onOpenPolicy }: PolicyDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();
  const { data: policy, isLoading } = useGetCortexPolicyQuery({ policyId });
  const [updatePolicy, { isLoading: isPatching }] = useUpdateCortexPolicyMutation();
  const [supersedePolicy, { isLoading: isSuperseding }] = useSupersedeCortexPolicyMutation();
  const [promotePolicy, { isLoading: isPromoting }] = usePromoteCortexPolicyMutation();
  const [deprecatePolicy, { isLoading: isDeprecating }] = useDeprecateCortexPolicyMutation();
  const isSaving = isPatching || isSuperseding;

  const isDraft = policy?.state === 'draft';
  const [title, setTitle] = useState<string | null>(null);
  const [bodyMd, setBodyMd] = useState<string | null>(null);
  const [policyTypeDraft, setPolicyTypeDraft] = useState<string | null>(null);
  // Metadata is round-tripped as raw JSON text so admins can hand-edit
  // arbitrary keys without a structured editor. Track parse errors so the
  // Save button can refuse a malformed payload.
  const [metadataText, setMetadataText] = useState<string | null>(null);
  const [metadataParseError, setMetadataParseError] = useState<string | null>(null);

  const effectiveTitle = title ?? policy?.title ?? '';
  const effectiveBody = bodyMd ?? policy?.body_md ?? '';
  const effectivePolicyType = policyTypeDraft ?? policy?.policy_type ?? '';
  const sourceMetadataText = policy
    ? JSON.stringify(policy.metadata || {}, null, 2)
    : '';
  const effectiveMetadataText = metadataText ?? sourceMetadataText;

  const metadataDirty = metadataText !== null && metadataText !== sourceMetadataText;
  const policyTypeDirty =
    policyTypeDraft !== null && policyTypeDraft !== (policy?.policy_type ?? '');
  const isDirty =
    (title !== null && title.trim() !== (policy?.title ?? '')) ||
    (bodyMd !== null && bodyMd !== (policy?.body_md ?? '')) ||
    metadataDirty ||
    policyTypeDirty;

  const handleMetadataChange = (text: string) => {
    setMetadataText(text);
    if (text.trim() === '') {
      setMetadataParseError(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setMetadataParseError('Metadata must be a JSON object.');
      } else {
        setMetadataParseError(null);
      }
    } catch (e: any) {
      setMetadataParseError(e?.message || 'Invalid JSON.');
    }
  };

  const missingRequiredMetadata = useMissingRequiredMetadataFields(
    effectivePolicyType || null,
    effectiveMetadataText,
  );
  // Block saves that would fail the type's schema (e.g. a cleared required
  // field) instead of letting the request 400. A pristine, already-valid
  // policy reports nothing missing, so this never blocks a plain promote.
  const saveBlocked = !!metadataParseError || missingRequiredMetadata.length > 0;

  /**
   * Persist edits. Returns the policy_id to operate on (own id for in-place
   * patches; the new draft's id for supersede). Returns null if nothing was
   * saved (no edits / parse error / API failure — caller stops).
   */
  const persistEdits = async (): Promise<string | null> => {
    if (!policy) return null;
    if (metadataParseError) return null;
    if (!isDirty) return policy.policy_id;

    const parsedMetadata = metadataDirty
      ? metadataText && metadataText.trim() !== ''
        ? JSON.parse(metadataText)
        : {}
      : undefined;
    try {
      if (isDraft) {
        const patch: {
          title?: string;
          body_md?: string;
          metadata?: Record<string, unknown>;
          policy_type?: string;
        } = {};
        if (title !== null && title.trim() !== policy.title) patch.title = title.trim();
        if (bodyMd !== null && bodyMd !== policy.body_md) patch.body_md = bodyMd;
        if (parsedMetadata !== undefined) patch.metadata = parsedMetadata;
        if (policyTypeDirty && effectivePolicyType) {
          patch.policy_type = effectivePolicyType;
        }
        await updatePolicy({
          policyId: policy.policy_id,
          updatePolicyRequest: patch,
        }).unwrap();
        return policy.policy_id;
      }
      const body: {
        title?: string;
        body_md?: string;
        metadata?: Record<string, unknown>;
        policy_type?: string;
      } = {};
      if (title !== null && title.trim() !== policy.title) body.title = title.trim();
      if (bodyMd !== null && bodyMd !== policy.body_md) body.body_md = bodyMd;
      if (parsedMetadata !== undefined) body.metadata = parsedMetadata;
      if (policyTypeDirty && effectivePolicyType) {
        body.policy_type = effectivePolicyType;
      }
      const newDraft = await supersedePolicy({
        policyId: policy.policy_id,
        supersedePolicyRequest: body,
      }).unwrap();
      return newDraft.policy_id;
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to save policy', { variant: 'error' });
      return null;
    }
  };

  const handleSave = async () => {
    const id = await persistEdits();
    if (!id || !policy) return;
    enqueueSnackbar(
      isDraft ? 'Policy updated' : 'New draft created — promote to activate',
      { variant: 'success' },
    );
    onClose();
  };

  const handleSaveAndPromote = async () => {
    const id = await persistEdits();
    if (!id) return;
    try {
      await promotePolicy({ policyId: id }).unwrap();
      enqueueSnackbar('Policy promoted to active', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to promote policy', { variant: 'error' });
    }
  };

  const handlePromoteOnly = async () => {
    if (!policy) return;
    try {
      await promotePolicy({ policyId: policy.policy_id }).unwrap();
      enqueueSnackbar('Policy promoted to active', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to promote policy', { variant: 'error' });
    }
  };

  const [deprecateConfirmOpen, setDeprecateConfirmOpen] = useState(false);

  const handleDeprecate = () => {
    if (!policy) return;
    setDeprecateConfirmOpen(true);
  };

  const confirmDeprecate = async () => {
    if (!policy) return;
    setDeprecateConfirmOpen(false);
    try {
      await deprecatePolicy({ policyId: policy.policy_id }).unwrap();
      enqueueSnackbar('Policy deprecated', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to deprecate policy', { variant: 'error' });
    }
  };

  return (
    <>
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isLoading || !policy
          ? 'Policy'
          : isDraft
            ? 'Edit Policy'
            : 'Edit Policy (will create a new draft)'}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
        {!isLoading && policy && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={policy.state}
                color={stateChipColor(policy.state)}
                size="small"
                variant="outlined"
              />
              {!isDraft && policy.policy_type && (
                <Chip
                  label={policy.policy_type}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
              )}
              {policy.applies_to && (
                <Tooltip
                  title="Consuming flow — stamped from the policy type; this policy is read by that flow at runtime."
                >
                  <Chip
                    label={`applies to: ${policy.applies_to}`}
                    color="info"
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Tooltip>
              )}
              {policy.scope === 'organization' && (
                <Tooltip title="Applies to this organization only.">
                  <Chip
                    label="org-scoped"
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              {policy.citations_verified && (
                <Chip label="Citations verified" color="success" size="small" variant="outlined" />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>
                  ID: <code>{policy.policy_id}</code>
                </span>
                <Tooltip title="Copy policy ID">
                  <IconButton
                    size="small"
                    onClick={() => copy(policy.policy_id, 'Policy ID')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Box>

            {policy.derived_via && (
              <Typography variant="caption" color="text.secondary">
                Provenance: <code>{policy.derived_via}</code>
              </Typography>
            )}

            {(policy.supersedes || (policy.superseded_by && policy.superseded_by.length > 0)) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {policy.supersedes && (
                  <Typography variant="caption">
                    <strong>Supersedes:</strong>{' '}
                    <Button
                      size="small"
                      variant="text"
                      sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                      onClick={() => policy.supersedes && onOpenPolicy(policy.supersedes.policy_id)}
                    >
                      {policy.supersedes.title}
                    </Button>
                    <Chip
                      label={policy.supersedes.state}
                      color={stateChipColor(policy.supersedes.state)}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                    />
                  </Typography>
                )}
                {policy.superseded_by && policy.superseded_by.length > 0 && (
                  <Typography variant="caption">
                    <strong>Superseded by:</strong>{' '}
                    {policy.superseded_by.map((ref, i) => (
                      <span key={ref.policy_id}>
                        {i > 0 && ', '}
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                          onClick={() => onOpenPolicy(ref.policy_id)}
                        >
                          {ref.title}
                        </Button>
                        <Chip
                          label={ref.state}
                          color={stateChipColor(ref.state)}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }}
                        />
                      </span>
                    ))}
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              label="Title"
              value={effectiveTitle}
              onChange={e => setTitle(e.target.value)}
              required
              fullWidth
              size="small"
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />

            {isDraft && (
              <PolicyTypeSelect
                value={effectivePolicyType}
                onChange={setPolicyTypeDraft}
                allowUntyped={!policy.policy_type}
              />
            )}

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block' }}
              >
                Body&nbsp;*
              </Typography>
              <TextField
                value={effectiveBody}
                onChange={e => setBodyMd(e.target.value)}
                fullWidth
                multiline
                minRows={10}
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.875rem' } } }}
              />
            </Box>

            {/* Metadata editor — schema-driven form for typed policies,
                raw JSON otherwise. Empty / blank means no metadata. Save
                is blocked when JSON is invalid. */}
            <PolicyMetadataEditor
              policyType={effectivePolicyType || null}
              metadataText={effectiveMetadataText}
              parseError={metadataParseError}
              onMetadataTextChange={handleMetadataChange}
            />

            {!isDraft && (
              <Alert severity="info">
                {policy.state === 'active' ? 'Active' : 'Deprecated'} policies are
                immutable. Saving will create a new draft pointing at this row via{' '}
                <code>supersedes_policy_id</code>; the original stays live until the
                new draft passes citation verification and is promoted.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          {isSaving || isPromoting || isDeprecating
            ? ''
            : metadataParseError
              ? 'Fix the metadata JSON to continue.'
              : missingRequiredMetadata.length > 0
                ? `Required: ${missingRequiredMetadata.join(', ')}`
                : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isSaving || isPromoting || isDeprecating}>
            Cancel
          </Button>

          {/* Draft with edits — split: "Save as draft" (secondary) +
              "Save & Promote" (primary). */}
          {isDraft && isDirty && (
            <>
              <Button
                variant="outlined"
                disabled={saveBlocked || isSaving || isPromoting || isDeprecating}
                onClick={handleSave}
              >
                {isSaving && !isPromoting ? <CircularProgress size={18} /> : 'Save as draft'}
              </Button>
              <Tooltip
                title={
                  policy?.supersedes
                    ? `Promoting will also deprecate: ${policy.supersedes.title}`
                    : 'Mark citations verified and promote to active'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={saveBlocked || isSaving || isPromoting || isDeprecating}
                    onClick={handleSaveAndPromote}
                  >
                    {isPromoting ? <CircularProgress size={18} /> : 'Save & Promote'}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          {/* Draft with no edits — one primary action: promote. */}
          {isDraft && !isDirty && (
            <Tooltip
              title={
                policy?.supersedes
                  ? `Promoting will also deprecate: ${policy.supersedes.title}`
                  : 'Mark citations verified and promote to active'
              }
            >
              <Button
                variant="contained"
                color="success"
                disabled={isSaving || isPromoting || isDeprecating}
                onClick={handlePromoteOnly}
              >
                {isPromoting ? <CircularProgress size={18} /> : 'Promote to active'}
              </Button>
            </Tooltip>
          )}

          {/* Active source — offer Deprecate alongside the save (supersede) button. */}
          {policy && policy.state === 'active' && (
            <Button
              color="warning"
              variant="outlined"
              disabled={isSaving || isPromoting || isDeprecating}
              onClick={handleDeprecate}
            >
              {isDeprecating ? <CircularProgress size={18} /> : 'Deprecate'}
            </Button>
          )}

          {/* Active / deprecated source — saving creates a new draft via
              supersede. Promotion happens on the next pass. */}
          {policy && !isDraft && (
            <Button
              variant="contained"
              disabled={!isDirty || saveBlocked || isSaving || isPromoting || isDeprecating}
              onClick={handleSave}
            >
              {isSaving ? <CircularProgress size={18} /> : 'Save as new draft'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>

    {/* Deprecate confirm — matches the dangerous-action dialog pattern used
        elsewhere in admin (e.g. org page). Avoids the native window.confirm
        which is blocked in some iframe / popup contexts. */}
    <Dialog
      open={deprecateConfirmOpen}
      onClose={() => setDeprecateConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Deprecate policy?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Deprecate <strong>&ldquo;{policy?.title}&rdquo;</strong>? Consumers will
          stop reading it. You can supersede with a new draft later if needed.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeprecateConfirmOpen(false)} disabled={isDeprecating}>
          Cancel
        </Button>
        <Button
          color="warning"
          variant="contained"
          onClick={confirmDeprecate}
          disabled={isDeprecating}
        >
          {isDeprecating ? <CircularProgress size={18} /> : 'Deprecate'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

/**
 * Required top-level metadata fields the typed policy is still missing.
 *
 * Lets the dialogs reflect the policy type's schema in the submit gate
 * (and surface what's missing) instead of deferring to a backend 400.
 * Returns field labels (schema title, falling back to the key). Untyped
 * policies, schemas without `required`, or unparseable JSON return []
 * — the latter is reported via the parse error instead.
 */
function useMissingRequiredMetadataFields(
  policyType: string | null,
  metadataText: string,
): string[] {
  const { data: schemaData } = useGetCortexPolicyTypeSchemaQuery(
    { policyType: policyType ?? '' },
    { skip: !policyType },
  );

  return useMemo(() => {
    const schema = policyType ? schemaData?.json_schema : undefined;
    const required: string[] = Array.isArray(schema?.required) ? schema!.required : [];
    if (required.length === 0) return [];

    let parsed: Record<string, unknown>;
    try {
      parsed = metadataText.trim() === '' ? {} : JSON.parse(metadataText);
    } catch {
      return [];
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return [];
    }

    const properties = (schema?.properties ?? {}) as Record<string, any>;
    return required
      .filter(key => {
        const value = parsed[key];
        return (
          value === undefined ||
          value === null ||
          (typeof value === 'string' && value.trim() === '')
        );
      })
      .map(key => properties[key]?.title ?? key);
  }, [policyType, schemaData, metadataText]);
}

interface PolicyMetadataEditorProps {
  policyType: string | null;
  metadataText: string;
  parseError: string | null;
  onMetadataTextChange: (text: string) => void;
  helperWhenValid?: string;
}

/**
 * Metadata editor with a schema-driven form for typed policies.
 *
 * The raw JSON text stays the single source of truth (the dialogs' parse
 * gating and dirty tracking are untouched); the form view parses it,
 * renders fields from the policy type's payload schema, and re-stringifies
 * on change. Untyped policies, unsupported schemas, and unparseable text
 * all fall back to the raw editor.
 */
function PolicyMetadataEditor({
  policyType,
  metadataText,
  parseError,
  onMetadataTextChange,
  helperWhenValid,
}: PolicyMetadataEditorProps) {
  const [view, setView] = useState<'form' | 'raw'>('form');
  const { data: schemaData } = useGetCortexPolicyTypeSchemaQuery(
    { policyType: policyType ?? '' },
    { skip: !policyType },
  );
  const schema = policyType ? schemaData?.json_schema : undefined;
  const formSupported = !!schema && isSchemaSupported(schema);

  const parsedValue = useMemo(() => {
    if (!formSupported || parseError) return null;
    try {
      const parsed = JSON.parse(metadataText.trim() === '' ? '{}' : metadataText);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }, [formSupported, parseError, metadataText]);

  const formAvailable = formSupported && parsedValue !== null;
  const effectiveView = formAvailable && view === 'form' ? 'form' : 'raw';

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Metadata
          {policyType
            ? ` (validated against the ${policyType} schema on save)`
            : ' (JSON object — consumer extras like JotAudit selectors, applicability filters, legacy_rule_id back-link, etc.)'}
        </Typography>
        {formAvailable && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={effectiveView}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="form" sx={{ py: 0.25 }}>
              Form
            </ToggleButton>
            <ToggleButton value="raw" sx={{ py: 0.25 }}>
              Raw JSON
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {effectiveView === 'form' && schema && parsedValue !== null ? (
        <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <SchemaForm
            schema={schema}
            value={parsedValue}
            onChange={next => onMetadataTextChange(JSON.stringify(next, null, 2))}
          />
        </Box>
      ) : (
        <TextField
          value={metadataText}
          onChange={e => onMetadataTextChange(e.target.value)}
          fullWidth
          multiline
          minRows={5}
          error={!!parseError}
          helperText={parseError || helperWhenValid || 'Leave empty for no metadata.'}
          placeholder='{ "applicability": { "cpt": ["99213"] } }'
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.8125rem' } } }}
        />
      )}
    </Box>
  );
}

const NO_FLOW = '__none__';

interface AppliesToSelectProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/** Flow picker — distinct applies_to values from the policy-type catalog,
 * plus a "None" choice for untyped / flow-less policies. */
function AppliesToSelect({ value, onChange, disabled }: AppliesToSelectProps) {
  const { data } = useListCortexPolicyTypesQuery();
  const flows = useMemo(() => {
    const distinct = new Set<string>();
    for (const t of data?.items ?? []) {
      if (t.applies_to) distinct.add(t.applies_to);
    }
    return Array.from(distinct).sort();
  }, [data]);

  return (
    <TextField
      select
      label="Applies to"
      value={value}
      onChange={e => onChange(e.target.value)}
      size="small"
      fullWidth
      disabled={disabled}
      helperText={
        value === NO_FLOW
          ? 'Not consumed by a runtime flow — free-form policy.'
          : 'The runtime flow that will read this policy.'
      }
    >
      <MenuItem value={NO_FLOW}>None (general policy)</MenuItem>
      {flows.map(flow => (
        <MenuItem key={flow} value={flow}>
          {flow}
        </MenuItem>
      ))}
    </TextField>
  );
}

interface PolicyTypeSelectProps {
  value: string;
  onChange: (next: string) => void;
  allowUntyped: boolean;
  disabled?: boolean;
  /** When set, only offer types feeding this flow (NO_FLOW = types with no
   * flow). Omit to offer every registered type. */
  flowFilter?: string;
}

/** Type picker fed by the policy-type registry catalog. */
function PolicyTypeSelect({
  value,
  onChange,
  allowUntyped,
  disabled,
  flowFilter,
}: PolicyTypeSelectProps) {
  const { data } = useListCortexPolicyTypesQuery();
  const allTypes = data?.items ?? [];
  const types =
    flowFilter === undefined
      ? allTypes
      : allTypes.filter(t =>
          flowFilter === NO_FLOW ? !t.applies_to : t.applies_to === flowFilter,
        );

  return (
    <TextField
      select
      label="Policy type"
      value={value}
      onChange={e => onChange(e.target.value)}
      size="small"
      fullWidth
      disabled={disabled}
      helperText={
        value
          ? allTypes.find(t => t.name === value)?.description
          : 'Untyped policies keep free-form metadata with no validation.'
      }
    >
      {allowUntyped && <MenuItem value="">Untyped (raw JSON)</MenuItem>}
      {types.map(t => (
        <MenuItem key={t.name} value={t.name}>
          {t.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
